import {useState, type Dispatch, type SetStateAction} from "react";
import {recalibrateGeometry, setGridOrigin, snapImagePointToGrid} from "@/dungeons/geometry/Coordinates.ts";
import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import type {GeometryDocumentUpdate} from "@/dungeons/editor/shared/useLevelGeometryDocument.ts";
import {rebaseDungeonViewport, type DungeonViewport} from "@/dungeons/editor/shared/DungeonViewport.ts";
import type {UnifiedEditorTool} from "@/dungeons/editor/shared/UnifiedEditorTools.ts";

export default function useGridCalibration({document, updateDocument, viewport, setViewport}: {
    document: LevelGeometryDocument;
    updateDocument: (update: GeometryDocumentUpdate) => void;
    viewport?: DungeonViewport;
    setViewport: Dispatch<SetStateAction<DungeonViewport | undefined>>;
}) {
    const [calibrationPoints, setCalibrationPoints] = useState<GridPoint[]>([]);
    const [originPoint, setOriginPoint] = useState<GridPoint>();
    const [cellCount, setCellCount] = useState(10);
    const [metersPerCell, setMetersPerCell] = useState(document.grid.metersPerCell);
    const pixelDistance = calibrationPoints.length === 2
        ? Math.max(Math.abs(calibrationPoints[1].x - calibrationPoints[0].x),
            Math.abs(calibrationPoints[1].y - calibrationPoints[0].y)) : 0;
    const pixelsPerCell = pixelDistance / cellCount;
    const canApplyCalibration = calibrationPoints.length === 2
        && Number.isInteger(cellCount) && cellCount > 0
        && Number.isFinite(pixelsPerCell) && pixelsPerCell > 0
        && Number.isFinite(metersPerCell) && metersPerCell > 0;

    const reset = () => {
        setCalibrationPoints([]);
        setOriginPoint(undefined);
    };
    const selectPoint = (point: GridPoint, tool: UnifiedEditorTool) => {
        if (tool === "origin" && document.registration.calibrated) {
            setOriginPoint(snapImagePointToGrid(point, document.registration));
        } else if (tool === "calibrate") {
            setCalibrationPoints(current => {
                if (current.length !== 1) return [point];
                const first = current[0];
                const horizontal = Math.abs(point.x - first.x) >= Math.abs(point.y - first.y);
                return [first, horizontal ? {x: point.x, y: first.y} : {x: first.x, y: point.y}];
            });
        }
    };
    const applyCalibration = () => {
        if (!canApplyCalibration) return false;
        const nextRegistration = {
            pixelsPerCell, gridOffsetX: calibrationPoints[0].x,
            gridOffsetY: calibrationPoints[0].y, calibrated: true,
        };
        updateDocument(current => recalibrateGeometry(current, nextRegistration, metersPerCell));
        if (viewport) setViewport(rebaseDungeonViewport(viewport, document.registration, nextRegistration));
        reset();
        return true;
    };
    const applyOrigin = () => {
        if (!originPoint) return false;
        updateDocument(current => setGridOrigin(current, originPoint));
        reset();
        return true;
    };
    return {
        calibrationPoints, originPoint, cellCount, metersPerCell, pixelsPerCell, canApplyCalibration,
        setCellCount, setMetersPerCell, setCalibrationPoints, setOriginPoint,
        reset, selectPoint, applyCalibration, applyOrigin,
    };
}
